/**
 * Per-child GRID PLACEMENT vocabulary — the curated `colSpan` / `rowSpan` values
 * a block may carry when it sits inside a layout parent (Grid / Columns).
 *
 * These are NOT component-schema props: the renderer (TreeRenderer) reads them
 * generically off each child node and applies `grid-column`/`grid-row` spans, so
 * child components stay span-unaware. Recipes set them; the editor + AI may set
 * them too (a child of a Grid can be widened/tallened). Centralized here so the
 * renderer, the op validator, the Inspector, and the AI spec never drift.
 */
export const LAYOUT_PARENT_NAMES = ["Grid", "Columns"] as const;
export const COL_SPAN_VALUES = ["1", "2", "3", "4"] as const;
export const ROW_SPAN_VALUES = ["1", "2"] as const;

export type ColSpan = (typeof COL_SPAN_VALUES)[number];
export type RowSpan = (typeof ROW_SPAN_VALUES)[number];

/** True when `name` is a layout container whose children carry spans. */
export function isLayoutParentName(name: string | undefined): boolean {
  return !!name && (LAYOUT_PARENT_NAMES as readonly string[]).includes(name);
}

/** Normalize a span value to its curated string key, or null if not allowed. */
export function colSpanKey(v: unknown): ColSpan | null {
  const k = typeof v === "number" ? String(v) : typeof v === "string" ? v : null;
  return k && (COL_SPAN_VALUES as readonly string[]).includes(k) ? (k as ColSpan) : null;
}
export function rowSpanKey(v: unknown): RowSpan | null {
  const k = typeof v === "number" ? String(v) : typeof v === "string" ? v : null;
  return k && (ROW_SPAN_VALUES as readonly string[]).includes(k) ? (k as RowSpan) : null;
}

/**
 * Generic per-BLOCK sizing: a width constraint + horizontal alignment a block may
 * carry regardless of its parent. Like spans, these are NOT component-schema
 * props — TreeRenderer reads them off the node and constrains/positions the
 * block's wrapper, so child components stay size-unaware. "full" = unconstrained
 * (the default); alignment only bites once a width narrows the block. Centralized
 * here so the renderer, the op validator, the Inspector, and the AI never drift.
 */
export const BLOCK_WIDTH_VALUES = ["full", "wide", "medium", "narrow"] as const;
export const BLOCK_ALIGN_VALUES = ["left", "center", "right"] as const;

export type BlockWidth = (typeof BLOCK_WIDTH_VALUES)[number];
export type BlockAlign = (typeof BLOCK_ALIGN_VALUES)[number];

/** maxWidth per width key ("full" is omitted — it applies no constraint). */
export const BLOCK_WIDTH_MAXW: Record<Exclude<BlockWidth, "full">, string> = {
  wide: "64rem",
  medium: "48rem",
  narrow: "32rem",
};

export function blockWidthKey(v: unknown): BlockWidth | null {
  return typeof v === "string" && (BLOCK_WIDTH_VALUES as readonly string[]).includes(v)
    ? (v as BlockWidth)
    : null;
}
export function blockAlignKey(v: unknown): BlockAlign | null {
  return typeof v === "string" && (BLOCK_ALIGN_VALUES as readonly string[]).includes(v)
    ? (v as BlockAlign)
    : null;
}
